import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import api from '../api/api';

const LOG_TYPES = ['SESSION', 'TRANSACTION', 'ERROR'];

export default function Logs() {
  const [logType, setLogType] = useState('SESSION');
  const [userId, setUserId]   = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [page, setPage]       = useState(1);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const { instance } = useMsal();

  const handleLogout = () => {
    sessionStorage.clear();
    const isMock = !instance.getActiveAccount();
    if (isMock) { window.location.href = '/'; } else { instance.logoutRedirect({ postLogoutRedirectUri: '/' }); }
  };

  const fetchLogs = async () => {
    setLoading(true); setMessage(''); setLogs([]); setPage(1);
    try {
      const fromISO = from ? new Date(from).toISOString() : undefined;
      const toISO   = to   ? new Date(to + 'T23:59:59').toISOString() : undefined;
      let data;
      if (userId.trim()) {
        const res = await api.get(`/logs/user/${userId.trim()}`, { params: { from: fromISO, to: toISO } });
        data = res.data;
      } else {
        const res = await api.get('/logs', { params: { type: logType, from: fromISO, to: toISO } });
        data = res.data;
      }
      setLogs(data);
      if (!data.length) setMessage('No logs found for the selected criteria.');
    } catch (err) { setMessage(err.response?.data?.error || 'Failed to fetch logs'); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <img src="/godrej-logo-dark.svg" alt="Godrej" style={s.logo} />
        <span style={s.headerTitle}>Form 16A — Log Viewer</span>
        <div style={s.headerRight}>
          <button style={s.linkBtn} onClick={() => navigate('/admin')}>← Admin</button>
          <button style={s.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div style={s.body}>
        <div style={s.card}>
          <div style={s.filterRow}>
            <div style={s.field}>
              <label style={s.label}>Log Type</label>
              <select style={s.select} value={logType} onChange={e => setLogType(e.target.value)}>
                {LOG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>User ID (optional)</label>
              <input style={s.input} value={userId} onChange={e => setUserId(e.target.value)} placeholder="Filter by user" />
            </div>
            <div style={s.field}>
              <label style={s.label}>From Date</label>
              <input style={s.input} type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.label}>To Date</label>
              <input style={s.input} type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            <button style={s.btn} onClick={fetchLogs} disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
          </div>
          {message && <p style={s.message}>{message}</p>}
        </div>

        {logs.length > 0 && (() => {
          const totalPages = Math.ceil(logs.length / PAGE_SIZE);
          const pageLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
          return (
            <div style={s.card}>
              <p style={s.count}>{logs.length} record(s) found — Page {page} of {totalPages}</p>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {['Timestamp','Type','User','Action','Detail'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageLogs.map((log, i) => (
                      <tr key={i} style={i % 2 === 0 ? s.rowEven : s.rowOdd}>
                        <td style={s.td}>{new Date(log.timestamp).toLocaleString()}</td>
                        <td style={s.td}><span style={{...s.badge, background: badgeColor(log.logType)}}>{log.logType}</span></td>
                        <td style={s.td}>{log.userId}</td>
                        <td style={s.td}>{log.action}</td>
                        <td style={s.td}><pre style={s.pre}>{JSON.stringify(log.detail, null, 2)}</pre></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={s.pagination}>
                <button className="rounded-pill" style={{...s.pgBtn, opacity: page === 1 ? 0.4 : 1}} onClick={() => setPage(p => p - 1)} disabled={page === 1}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} className="rounded-pill" style={{...s.pgBtn, background: n === page ? 'var(--brand-purple)' : '#fff', color: n === page ? '#fff' : 'var(--brand-purple)'}} onClick={() => setPage(n)}>{n}</button>
                ))}
                <button className="rounded-pill" style={{...s.pgBtn, opacity: page === totalPages ? 0.4 : 1}} onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next →</button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

const badgeColor = (type) => ({ SESSION: 'var(--brand-purple)', TRANSACTION: '#2e7d32', ERROR: '#c62828' }[type] || '#555');

const s = {
  page:       { minHeight: '100vh', background: '#fafafa' },
  header:     { height: '56px', padding: '0 2rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #F1F1ED', background: '#fff' },
  logo:       { height: '28px' },
  headerTitle:{ fontFamily: 'var(--font-headline)', fontSize: '1.1rem', color: '#333', flex: 1 },
  headerRight:{ display: 'flex', alignItems: 'center', gap: '12px' },
  logoutBtn:  { background: 'transparent', border: '1px solid var(--brand-purple)', color: 'var(--brand-purple)', padding: '4px 14px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '13px' },
  linkBtn:    { background: 'transparent', border: 'none', color: 'var(--brand-purple)', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px', fontFamily: 'var(--font-body)' },
  body:       { padding: '24px', maxWidth: '1100px', margin: '0 auto' },
  card:       { background: '#fff', padding: '20px', border: '1px solid #F1F1ED', marginBottom: '20px' },
  filterRow:  { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  field:      { flex: 1, minWidth: '140px' },
  label:      { display: 'block', fontSize: '13px', color: 'var(--brand-grey)', marginBottom: '4px' },
  select:     { width: '100%', height: '50px', padding: '0 10px', border: '1px solid #F1F1ED', fontSize: '14px', color: 'var(--brand-grey)' },
  input:      { width: '100%', height: '50px', padding: '0 15px', border: '1px solid #F1F1ED', fontSize: '14px', color: 'var(--brand-grey)', boxSizing: 'border-box' },
  btn:        { padding: '10px 24px', background: 'var(--brand-purple)', color: '#fff', border: 'none', fontFamily: 'var(--font-highlight)', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  message:    { marginTop: '12px', fontSize: '13px', color: 'var(--brand-grey)' },
  count:      { fontSize: '13px', color: 'var(--brand-grey)', marginBottom: '8px' },
  tableWrap:  { overflowX: 'auto' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'var(--font-body)' },
  th:         { background: '#fafafa', padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #F1F1ED', whiteSpace: 'nowrap', color: '#333' },
  td:         { padding: '8px 12px', borderBottom: '1px solid #F1F1ED', verticalAlign: 'top', color: 'var(--brand-grey)' },
  rowEven:    { background: '#fff' },
  rowOdd:     { background: '#fafafa' },
  badge:      { color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontFamily: 'var(--font-highlight)' },
  pre:        { margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap', maxWidth: '300px' },
  pagination:  { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' },
  pgBtn:       { padding: '6px 14px', border: '1px solid var(--brand-purple)', background: '#fff', color: 'var(--brand-purple)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--font-body)', borderRadius: '50px' },
};
